import { pricing } from '../data/pricing'
import { serviceAreas, type ServiceAreaId } from '../data/serviceAreas'
import type { ServiceId } from '../data/services'

export type FieldInspectionType = keyof typeof pricing.fieldInspections
export type ApostillePackage = 'full' | 'limited'

export interface QuoteInput {
  serviceId: ServiceId
  areaId: ServiceAreaId
  signers: number
  stamps: number
  documents: number
  courierNeeded: boolean
  fieldInspectionType?: FieldInspectionType
  apostillePackage?: ApostillePackage
}

export interface QuoteResult {
  status: 'estimate' | 'quote-required'
  total?: number
  serviceSubtotal?: number
  travelFee?: number
  message: string
}

const nonNegativeInteger = (value: number) =>
  Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0

export function calculateQuote(input: QuoteInput): QuoteResult {
  const area = serviceAreas.find((candidate) => candidate.id === input.areaId)
  if (!area) {
    return {
      status: 'quote-required',
      message: 'Select a service area to continue.',
    }
  }

  if (area.hourlyTravel) {
    return {
      status: 'quote-required',
      message: 'Custom travel quote required for this area.',
    }
  }

  if (input.courierNeeded && input.serviceId !== 'document-courier') {
    return {
      status: 'quote-required',
      message: 'Courier coordination requires a custom route and timing quote.',
    }
  }

  const signers = nonNegativeInteger(input.signers)
  const stamps = nonNegativeInteger(input.stamps)
  const documents = nonNegativeInteger(input.documents)
  let serviceSubtotal = 0

  switch (input.serviceId) {
    case 'mobile-notary':
      if (signers < 1 || stamps < 1) {
        return {
          status: 'quote-required',
          message: 'Enter at least one signer and one notarization.',
        }
      }
      serviceSubtotal =
        signers * pricing.traditionalNotary.perSigner +
        stamps * pricing.traditionalNotary.perStamp
      break
    case 'e-notary':
      if (signers < 1 || stamps < 1) {
        return {
          status: 'quote-required',
          message: 'Enter at least one signer and one notarization.',
        }
      }
      serviceSubtotal =
        signers * pricing.eNotary.perSigner + stamps * pricing.eNotary.perStamp
      break
    case 'loan-signing':
      serviceSubtotal = pricing.loanSigning
      break
    case 'trust-estate-signing':
      serviceSubtotal = pricing.trustEstateSigning
      break
    case 'i9-verification':
      serviceSubtotal = pricing.i9Verification
      break
    case 'field-inspections':
      if (!input.fieldInspectionType) {
        return {
          status: 'quote-required',
          message: 'Select an inspection type to calculate a preliminary estimate.',
        }
      }
      serviceSubtotal = pricing.fieldInspections[input.fieldInspectionType]
      break
    case 'certified-copies':
      if (documents < 1) {
        return {
          status: 'quote-required',
          message: 'Enter at least one document.',
        }
      }
      serviceSubtotal = documents * pricing.certifiedCopy
      break
    case 'apostille-assistance':
      if (documents < 1 || !input.apostillePackage) {
        return {
          status: 'quote-required',
          message: 'Select a package and enter the number of documents.',
        }
      }
      serviceSubtotal =
        input.apostillePackage === 'full'
          ? pricing.apostilleAssistance.fullPackageBase +
            documents * pricing.apostilleAssistance.fullPackagePerDocument
          : documents * pricing.apostilleAssistance.limitedPerDocument
      break
    case 'document-courier':
      return {
        status: 'quote-required',
        message: 'Courier pricing depends on the pickup, destination, timing, and route.',
      }
  }

  const total = serviceSubtotal + area.travelFee
  return {
    status: 'estimate',
    total,
    serviceSubtotal,
    travelFee: area.travelFee,
    message:
      input.serviceId === 'e-notary'
        ? 'Platform fees may apply in addition to this estimate.'
        : input.serviceId === 'apostille-assistance'
          ? 'Government, shipping, and third-party fees are not included.'
          : 'Final service and travel fees will be confirmed before scheduling.',
  }
}
